import React, { useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, Clock, ChevronRight } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import WhatsappFloat from "@/components/site/WhatsappFloat";
import QuoteDialog from "@/components/site/QuoteDialog";
import SEO from "@/components/seo/SEO";
import { BLOG_POSTS, BLOG_CATEGORIES } from "@/data/blog";

const META_ICON_SIZE_PX = 13;

// Static motion props — declared once at module scope for reference stability.
const CARD_INITIAL = { opacity: 0, y: 18 };
const CARD_WHILE_IN_VIEW = { opacity: 1, y: 0 };
const CARD_VIEWPORT = { once: true, amount: 0.2 };
const cardTransition = (i) => ({ duration: 0.5, delay: i * 0.06 });

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
};

export default function BlogIndex() {
  const [open, setOpen] = useState(false);
  const [params, setParams] = useSearchParams();
  const activeCat = params.get("cat") || "todos";

  const posts = useMemo(() => {
    if (activeCat === "todos") return BLOG_POSTS;
    return BLOG_POSTS.filter((p) => p.category === activeCat);
  }, [activeCat]);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "Blog M NAIEF Brindes Personalizados",
    description: "Conteúdo sobre brindes corporativos, sacolas de algodão personalizadas, eventos e branding.",
    blogPost: posts.map((p) => ({
      "@type": "BlogPosting",
      headline: p.title,
      datePublished: p.date,
      author: { "@type": "Person", name: p.author },
      url: `https://mnaief.com.br/blog/${p.slug}`,
    })),
  };

  return (
    <div className="App" data-testid="blog-index-page">
      <SEO
        title="Blog — Brindes Corporativos, Sacolas de Algodão, Branding e Sustentabilidade"
        description="Conteúdo prático sobre brindes corporativos personalizados, sacolas de algodão, eventos, branding e sustentabilidade empresarial."
        keywords="blog brindes corporativos, blog sacolas de algodão, marketing promocional, sustentabilidade empresarial, branding"
        url="/blog"
        type="website"
        jsonLd={jsonLd}
      />
      <Header onQuoteClick={() => setOpen(true)} />

      <main className="pt-[120px] lg:pt-[180px] pb-20 bg-white">
        <div className="mn-container">
          <nav className="text-xs uppercase tracking-[0.2em] text-[var(--mn-dark)] flex items-center gap-2 mb-6" aria-label="breadcrumb">
            <Link to="/" className="hover:text-[var(--mn-gold)]">Home</Link>
            <ChevronRight size={12} />
            <span className="text-[var(--mn-gold)]">Blog</span>
          </nav>

          <div className="max-w-3xl">
            <span className="mn-eyebrow">Conteúdo</span>
            <h1 className="mn-h-display text-[2.5rem] sm:text-[3rem] lg:text-[3.5rem] mt-4 leading-[1.05]">
              Blog <span style={{ color: "var(--mn-gold)" }}>M NAIEF</span>
            </h1>
            <p className="mt-5 text-[var(--mn-dark)] text-base sm:text-lg leading-relaxed">
              Dicas, tendências e estratégias para usar brindes corporativos como ferramenta de marketing, branding e sustentabilidade.
            </p>
          </div>

          {/* Filters */}
          <div className="mt-10 flex flex-wrap gap-2" data-testid="blog-filters">
            <button
              onClick={() => setParams({})}
              className={`text-xs uppercase tracking-[0.18em] font-semibold px-4 py-2 rounded-sm border transition-all ${
                activeCat === "todos"
                  ? "bg-[var(--mn-black)] text-white border-[var(--mn-black)]"
                  : "bg-white text-[var(--mn-dark)] border-[var(--mn-line)] hover:border-[var(--mn-gold)] hover:text-[var(--mn-gold)]"
              }`}
              data-testid="blog-filter-todos"
            >
              Todos
            </button>
            {BLOG_CATEGORIES.map((c) => (
              <button
                key={c.slug}
                onClick={() => setParams({ cat: c.slug })}
                className={`text-xs uppercase tracking-[0.18em] font-semibold px-4 py-2 rounded-sm border transition-all ${
                  activeCat === c.slug
                    ? "bg-[var(--mn-black)] text-white border-[var(--mn-black)]"
                    : "bg-white text-[var(--mn-dark)] border-[var(--mn-line)] hover:border-[var(--mn-gold)] hover:text-[var(--mn-gold)]"
                }`}
                data-testid={`blog-filter-${c.slug}`}
              >
                {c.name}
              </button>
            ))}
          </div>

          {/* Grid */}
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-7" data-testid="blog-grid">
            {posts.map((p, i) => {
              const cat = BLOG_CATEGORIES.find((c) => c.slug === p.category);
              return (
                <motion.article
                  key={p.slug}
                  initial={CARD_INITIAL}
                  whileInView={CARD_WHILE_IN_VIEW}
                  viewport={CARD_VIEWPORT}
                  transition={cardTransition(i)}
                  className="border border-[var(--mn-line)] rounded-[4px] overflow-hidden hover:border-[var(--mn-gold)] hover:shadow-[0_22px_50px_-30px_rgba(0,0,0,0.18)] transition-all bg-white"
                  data-testid={`blog-card-${p.slug}`}
                >
                  <Link to={`/blog/${p.slug}`} className="block group">
                    <div
                      className="aspect-square overflow-hidden flex items-center justify-center"
                      style={{ background: "#F5F5F5", padding: "16px" }}
                    >
                      <img
                        src={p.cover}
                        alt={p.title}
                        loading="lazy"
                        className="max-w-full max-h-full w-auto h-auto object-contain"
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          transition: "transform 0.3s ease",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.12)")}
                        onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
                      />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.22em] font-semibold mb-3">
                        <span className="text-[var(--mn-gold)]">{cat?.name || "Geral"}</span>
                      </div>
                      <h2 className="font-serif-display font-bold text-xl text-[var(--mn-black)] leading-snug group-hover:text-[var(--mn-gold)] transition-colors">
                        {p.title}
                      </h2>
                      <p className="mt-3 text-sm text-[var(--mn-dark)] leading-relaxed line-clamp-3">{p.excerpt}</p>
                      <div className="mt-5 flex items-center gap-4 text-xs text-[var(--mn-dark)]">
                        <span className="inline-flex items-center gap-1.5"><Calendar size={META_ICON_SIZE_PX} /> {formatDate(p.date)}</span>
                        <span className="inline-flex items-center gap-1.5"><Clock size={META_ICON_SIZE_PX} /> {p.readTime}</span>
                      </div>
                    </div>
                  </Link>
                </motion.article>
              );
            })}
          </div>

          {posts.length === 0 && (
            <p className="text-center text-[var(--mn-dark)] py-16">Nenhum artigo encontrado nessa categoria.</p>
          )}
        </div>
      </main>

      <Footer />
      <WhatsappFloat />
      <QuoteDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}
